import { useState, useEffect } from "react";
import { Menu, X, Phone, MessageCircle, Globe } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BRAND, LOGO_URL } from "@/lib/constants";

const navKeys = ["home", "services", "beforeAfter", "howItWorks", "faq", "location", "quote", "contact"];
const navTargets = {
  home: "#home",
  services: "#services",
  beforeAfter: "#gallery",
  howItWorks: "#how",
  faq: "#faq",
  location: "#location",
  quote: "#quote",
  contact: "#contact",
};

const Header = () => {
  const { t, toggleLang, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.location.href = "/" + href;
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-black/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
        {/* Logo */}
        <a
          href="#home"
          onClick={(e) => { e.preventDefault(); handleNavClick("#home"); }}
          className="flex items-center gap-3 shrink-0"
          data-testid="header-logo-link"
        >
          <span className="bg-white rounded-2xl px-3 py-1.5 shadow-lg shadow-red-900/10 flex items-center">
            <img
              src={LOGO_URL}
              alt="Dent Xpress"
              className="h-10 w-auto object-contain"
              draggable={false}
            />
          </span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8 text-sm font-semibold text-white/80">
          {navKeys.map((k) => (
            <a
              key={k}
              href={navTargets[k]}
              onClick={(e) => { e.preventDefault(); handleNavClick(navTargets[k]); }}
              className="hover:text-white transition relative group"
              data-testid={`nav-${k}`}
            >
              {t.nav[k]}
              <span className="absolute -bottom-1 inset-x-0 h-[2px] bg-[#e10600] scale-x-0 group-hover:scale-x-100 origin-left transition-transform" />
            </a>
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <button
            onClick={toggleLang}
            className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-full border border-white/15 text-white/90 hover:border-white/40 hover:text-white transition text-xs font-bold tracking-widest font-display"
            data-testid="lang-switcher"
            aria-label="Toggle language"
          >
            <Globe className="w-3.5 h-3.5" />
            {t.common.langLabel}
          </button>

          <a
            href={BRAND.whatsappLink}
            target="_blank"
            rel="noreferrer"
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#e10600] hover:bg-[#c20500] text-white font-bold text-sm transition shadow-lg shadow-red-900/30"
            data-testid="header-whatsapp-btn"
          >
            <MessageCircle className="w-4 h-4" />
            {t.common.whatsapp}
          </a>

          <a
            href={BRAND.telLink}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 sm:px-4 sm:py-2.5 min-w-[44px] min-h-[44px] rounded-full border border-white/20 text-white hover:bg-white/5 font-bold text-sm transition"
            data-testid="header-call-btn"
            aria-label={t.common.call}
          >
            <Phone className="w-4 h-4" />
            <span className="hidden sm:inline">{t.common.call}</span>
          </a>

          <button
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/15 text-white"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {open && (
        <div className="lg:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col gap-1">
            <div className="flex items-center justify-between mb-4">
              <span className="bg-white rounded-xl px-3 py-1.5">
                <img src={LOGO_URL} alt="Dent Xpress" className="h-9 w-auto" />
              </span>
              <button
                onClick={toggleLang}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-white/15 text-white/90 text-xs font-bold font-display"
                data-testid="mobile-lang-switcher"
              >
                <Globe className="w-3.5 h-3.5" />
                {t.common.langLabel}
              </button>
            </div>
            {navKeys.map((k) => (
              <a
                key={k}
                href={navTargets[k]}
                onClick={(e) => { e.preventDefault(); handleNavClick(navTargets[k]); }}
                className="px-3 py-3 rounded-xl text-white/85 hover:bg-white/5 font-semibold"
                data-testid={`mobile-nav-${k}`}
              >
                {t.nav[k]}
              </a>
            ))}
            <div className="grid grid-cols-2 gap-3 mt-4">
              <a href={BRAND.whatsappLink} target="_blank" rel="noreferrer"
                 className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#e10600] hover:bg-[#c20500] text-white font-bold text-sm transition"
                 data-testid="mobile-whatsapp-btn">
                <MessageCircle className="w-4 h-4" /> {t.common.whatsapp}
              </a>
              <a href={BRAND.telLink}
                 className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-white/20 text-white font-bold text-sm transition"
                 data-testid="mobile-call-btn">
                <Phone className="w-4 h-4" /> {t.common.call}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
